/**
 * 高知県の背景ツアー「高知めぐり」コンテンツ。
 *
 * 軸は一日の時間。夜明けの室戸岬から、月の出た桂浜へ。
 *
 * 桂浜について。
 *  坂本龍馬像は彫刻としての権利の扱いが微妙なので、画面に入れない。
 *  さらに龍王岬の上には海津見神社（龍王宮）と早高神社の祠がある。岬も画面に入れない。
 *  弓なりの砂浜と波と崖の上の松と月だけで構成する。
 *
 * 鍾乳洞について。
 *  岡山（満奇洞）と山口（秋芳洞の言及）で扱っているので、龍河洞は使わない。
 *  かわりに伊尾木洞を採る。こちらは鍾乳洞ではなく、波の浸食でできた海食洞とシダの谷で、
 *  緑のシダに覆われた濡れた岩の回廊という、まったく別の絵になる。
 *
 * 灯台について。
 *  灯台は千葉2、沖縄1、島根1、愛媛1ですでに使っている。
 *  室戸岬も足摺岬も、灯台は画面に入れない。岩礁と亜熱帯の植物と断崖で構成する。
 *
 * 「仁淀ブルー」について。
 *  この呼び名を使っているのは、流域6市町村でつくる一般社団法人仁淀ブルー観光協議会と
 *  いの町観光協会。高知県庁の公式での使用は確認できていない。
 *  だから「高知県が言っている」とは書かない。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 「日本三大鍾乳洞」（龍河洞）は国・県の断定形が確認できない。
 *  - 竜串の「シコロサンゴの群落は日本一の規模」は市公式が「とされています」という伝聞形。
 *  - 高知城は「天守と本丸御殿がともに現存する唯一の城」とは書かない。公式の断定形をそのまま使う。
 *  - 桂浜の「五色の小石」は高知市公式に記載がない。書かない。
 *  - 四万十川全体の沈下橋の本数は公的な出典が取れない。「四万十市内に9本」だけを書く。
 *  - 北川村のモネの庭は作品由来の施設。扱わない。ひろめ市場は民間施設なので扱わない。
 */

import type { StageTour } from '../types';

export const kochiSceneryTour: StageTour = {
  scenes: [
    {
      id: 'muroto-misaki',
      name: '室戸岬',
      area: '室戸市・夜明け',
      image: '/assets/scenery/kochi/scenes/01_muroto_misaki.webp',
      hotspots: [
        {
          id: 'muroto-vji',
          x: 0.46,
          y: 0.42,
          title: '太平洋にV字に突き出す',
          body: '室戸市の公式サイトは、室戸岬を、県都高知市の東方78キロに位置し、太平洋にV字形に突出した日本八景の岬と説明しています。市は東西53.3キロの海岸線を持ち、面積の八割以上を山林が占めます。',
          source: 'https://www.city.muroto.kochi.jp/pages/gaiyo.php',
          mapQuery: '室戸岬 高知県室戸市室戸岬町',
        },
        {
          id: 'muroto-anettai',
          x: 0.74,
          y: 0.34,
          title: '岬をおおう亜熱帯の緑',
          body: '室戸市の公式サイトによれば、室戸岬の亜熱帯性樹林や海岸植物は国の天然記念物となり、岬一帯は昭和3年に名勝の指定を受けています。ハマユウ、アコウ、アオギリなどが育っています。',
          source: 'https://www.city.muroto.kochi.jp/pages/gaiyo.php',
          mapQuery: '室戸岬 亜熱帯性樹林',
        },
        {
          id: 'muroto-geopark',
          x: 0.22,
          y: 0.5,
          title: '世界ジオパーク認定',
          body: '室戸ユネスコ世界ジオパークの公式サイトによれば、2011年9月18日、美しい自然とそれを守りながら活かす活動が高く評価され、世界ジオパークネットワークへの加盟が認定されました。',
          source: 'https://www.muroto-geo.jp/about/',
          mapQuery: '室戸世界ジオパークセンター',
        },
      ],
    },
    {
      id: 'sada-chinkabashi',
      name: '四万十川 佐田沈下橋',
      area: '四万十市・朝もや',
      image: '/assets/scenery/kochi/scenes/02_sada_chinkabashi.webp',
      hotspots: [
        {
          id: 'sada-rankan',
          x: 0.46,
          y: 0.46,
          title: '欄干がない理由',
          body: '四万十市観光協会の公式サイトによれば、沈下橋に欄干がないのは、水の抵抗を受けにくくするため、そして欄干があることで木などが引っかかり水の流れが悪くなるのを防ぐためです。',
          source: 'https://www.shimanto-kankou.com/kanko/shimanto-river/shimantoriver.html',
          mapQuery: '佐田沈下橋 四万十市',
        },
        {
          id: 'sada-291m',
          x: 0.74,
          y: 0.36,
          title: 'いちばん下流で最長',
          body: '四万十市観光協会の公式サイトは、佐田沈下橋を四万十川最下流で最長の沈下橋と説明しています。長さは291.6メートル、架けられたのは1972年です。正式な名前は今成橋で、佐田沈下橋は通称です。',
          source: 'https://www.shimanto-kankou.com/kanko/shimanto-river/shimantoriver.html',
          mapQuery: '今成橋 四万十市佐田',
        },
        {
          id: 'sada-196km',
          x: 0.22,
          y: 0.5,
          title: '四国でいちばん長い川',
          body: '四万十市観光協会の公式サイトによれば、四万十川は196キロにもわたる四国最長の大河です。もとの名前は渡川で、1994年に四万十川へ改められました。四万十市内だけでも、佐田のほかに高瀬、勝間、岩間などの沈下橋があります。',
          source: 'https://www.shimanto-kankou.com/kanko/shimanto-river/shimantoriver.html',
          mapQuery: '四万十川 沈下橋',
        },
      ],
    },
    {
      id: 'iokido',
      name: '伊尾木洞',
      area: '安芸市・初夏の午前',
      image: '/assets/scenery/kochi/scenes/03_iokido.webp',
      hotspots: [
        {
          id: 'iokido-nami',
          x: 0.46,
          y: 0.44,
          title: '波が掘った洞穴',
          body: '安芸市の公式サイトによれば、伊尾木洞は太平洋からの波により浸食されてできたものです。高さはおよそ5メートル、幅およそ4メートル、延長はおよそ40メートル。薄暗い洞内には、足元を浸すほどの谷水が流れています。',
          source: 'https://www.city.aki.kochi.jp/life/dtl.php?hdnKey=47',
          mapQuery: '伊尾木洞 高知県安芸市伊尾木',
        },
        {
          id: 'iokido-shida',
          x: 0.74,
          y: 0.34,
          title: '七種類のシダの群落',
          body: '安芸市の公式サイトによれば、洞の中と奥の谷にはホウビシダ、ホウライシダ、ノコギリシダ、コモチシダ、クリハラン、シロヤマゼンマイ、マツザカシダの七種類のシダが群生しています。',
          source: 'https://www.city.aki.kochi.jp/life/dtl.php?hdnKey=47',
          mapQuery: '伊尾木洞シダ群落',
        },
        {
          id: 'iokido-tennen',
          x: 0.22,
          y: 0.5,
          title: '大正時代からの天然記念物',
          body: '安芸市の公式サイトによれば、伊尾木洞のシダ群落は1926年、大正15年に国の天然記念物に指定されています。観光案内所では長靴の無料レンタルがあり、夏場のヘビ対策としてすすめられています。',
          source: 'https://www.city.aki.kochi.jp/life/dtl.php?hdnKey=47',
          mapQuery: '伊尾木洞 観光案内所',
        },
      ],
    },
    {
      id: 'nikobuchi',
      name: 'にこ淵',
      area: 'いの町・正午',
      image: '/assets/scenery/kochi/scenes/04_nikobuchi.webp',
      hotspots: [
        {
          id: 'nikobuchi-daija',
          x: 0.46,
          y: 0.44,
          title: '水神の大蛇が棲むという淵',
          body: 'いの町観光協会の公式サイトによれば、にこ淵には水神の化身とされる大蛇が棲む所という伝説が残り、今も地元の人にとって神聖な場所です。入水や飲食、喫煙などは遠慮して、静かに見るよう求められています。',
          source: 'https://www.inofan.jp/spot/recommended/n497/',
          mapQuery: 'にこ淵 高知県吾川郡いの町清水上分',
        },
        {
          id: 'nikobuchi-edagawa',
          x: 0.74,
          y: 0.34,
          title: '本流ではなく支流にある',
          body: 'いの町観光協会の公式サイトによれば、にこ淵は仁淀川の支流にあたる枝川川にあります。見るたびに変化する幻想的な青と紹介されており、太陽の光が注ぐ時間や季節による光の角度で色が変わります。',
          source: 'https://www.inofan.jp/spot/recommended/n497/',
          mapQuery: '枝川川 にこ淵',
        },
        {
          id: 'nikobuchi-124km',
          x: 0.22,
          y: 0.5,
          title: '石鎚山から124キロ',
          body: '仁淀川流域6市町村でつくる一般社団法人仁淀ブルー観光協議会は、仁淀川を、西日本最高峰石鎚山に流れを発し、124キロもの永い旅を経てやがて太平洋へと注ぐ紺碧の流れと表現しています。',
          source: 'https://niyodoblue.jp/',
          mapQuery: '仁淀川',
        },
      ],
    },
    {
      id: 'kashiwajima',
      name: '柏島',
      area: '大月町・夏の真昼',
      image: '/assets/scenery/kochi/scenes/05_kashiwajima.webp',
      hotspots: [
        {
          id: 'kashiwajima-sango',
          x: 0.46,
          y: 0.44,
          title: '澄んだ海と多様な生き物',
          body: '環境省の足摺宇和海国立公園の見どころ紹介によれば、柏島は周囲4キロの小さな島です。潮流による澄んだ海と、黒潮と豊後水道の影響を受けた多様な生き物が育まれ、ダイビング客にも人気があります。',
          source: 'https://www.env.go.jp/nature/nationalparks/list/ashizuri-uwakai/spot/',
          mapQuery: '柏島 高知県幡多郡大月町柏島',
        },
        {
          id: 'kashiwajima-anettai',
          x: 0.74,
          y: 0.34,
          title: '亜熱帯の魚がすむサンゴ',
          body: '環境省の足摺宇和海国立公園の公式ページによれば、この海域ではイシサンゴ類などの造礁サンゴが発達し、これらサンゴの間にソラスズメダイやチョウチョウウオなど亜熱帯性の魚類が生息しています。',
          source: 'https://www.env.go.jp/park/ashizuri/point/index.html',
          mapQuery: '足摺宇和海国立公園 柏島',
        },
        {
          id: 'kashiwajima-biroujima',
          x: 0.22,
          y: 0.5,
          title: '沖の島は海鳥の繁殖地',
          body: '環境省の公式ページによれば、柏島のすぐ沖合の蒲葵島にはオオミズナギドリの繁殖地が形成されています。足摺宇和海国立公園が指定されたのは昭和47年11月10日です。',
          source: 'https://www.env.go.jp/park/ashizuri/point/index.html',
          mapQuery: '大堂山展望台 大月町',
        },
      ],
    },
    {
      id: 'kochi-jo',
      name: '高知城',
      area: '高知市・春の午後',
      image: '/assets/scenery/kochi/scenes/06_kochi_jo.webp',
      hotspots: [
        {
          id: 'kochijo-honmaru',
          x: 0.46,
          y: 0.38,
          title: '本丸御殿が天守につながる',
          body: '高知城の公式サイトは、本丸御殿が天守に接続する形式を残すのは高知城だけであり、貴重な遺構になります、と書いています。高知城は、江戸時代以前から残る現存天守十二城のひとつでもあります。',
          source: 'https://kochipark.jp/kochijyo/highlights',
          mapQuery: '高知城 高知県高知市丸ノ内',
        },
        {
          id: 'kochijo-otemon',
          x: 0.74,
          y: 0.5,
          title: '天守と追手門がそろう',
          body: '高知城の公式サイトによれば、追手門と天守がともに残っているのは、高知城、丸亀城、弘前城のわずか三城のみです。',
          source: 'https://kochipark.jp/kochijyo/highlights',
          mapQuery: '高知城 追手門',
        },
        {
          id: 'kochijo-15mune',
          x: 0.22,
          y: 0.48,
          title: '15棟が国の重要文化財',
          body: '高知城の公式サイトによれば、天守、懐徳館すなわち本丸御殿、納戸蔵、黒鉄門、西多聞、東多聞、詰門、廊下門、追手門と六棟の矢狭間塀の15棟が、昭和9年1月30日に重要文化財に指定されています。',
          source: 'https://kochipark.jp/kochijyo/important',
          mapQuery: '高知公園 高知城',
        },
      ],
    },
    {
      id: 'nakatsu-keikoku',
      name: '中津渓谷',
      area: '仁淀川町・秋の午後',
      image: '/assets/scenery/kochi/scenes/07_nakatsu_keikoku.webp',
      hotspots: [
        {
          id: 'nakatsu-13km',
          x: 0.46,
          y: 0.44,
          title: 'およそ1.3キロの遊歩道',
          body: '仁淀川町の公式サイトによれば、中津渓谷にはおよそ1.3キロにもおよぶ遊歩道が整備されています。県立自然公園に指定されており、燃えるようなもみじで知られる名所です。',
          source: 'https://www.town.niyodogawa.lg.jp/kanko/life_dtl.php?hdnKey=945',
          mapQuery: '中津渓谷 高知県吾川郡仁淀川町名野川',
        },
        {
          id: 'nakatsu-uryu',
          x: 0.74,
          y: 0.32,
          title: '落差20メートルの滝',
          body: '仁淀川町の公式サイトによれば、渓谷の奥深くにある雨竜の滝は中津渓谷のシンボルで、落差は20メートルです。多方向へ勢いよく吹き出す水の雄大な姿から、「竜吐水」とも呼ばれています。',
          source: 'https://www.town.niyodogawa.lg.jp/kanko/life_dtl.php?hdnKey=945',
          mapQuery: '雨竜の滝 中津渓谷',
        },
        {
          id: 'nakatsu-ryugubuchi',
          x: 0.22,
          y: 0.5,
          title: '11月ごろが紅葉の盛り',
          body: '仁淀川町の公式サイトによれば、展望台から100メートルほど登って雨竜の滝の上流にある竜宮渕は、11月ごろ紅葉の絶景ポイントになります。春の水、夏の清流、秋の紅葉、冬の冷気と、季節ごとに表情を変える渓谷です。',
          source: 'https://www.town.niyodogawa.lg.jp/kanko/life_dtl.php?hdnKey=945',
          mapQuery: '竜宮渕 中津渓谷',
        },
      ],
    },
    {
      id: 'doi-kachu',
      name: '土居廓中',
      area: '安芸市・夕方前',
      image: '/assets/scenery/kochi/scenes/08_doi_kachu.webp',
      hotspots: [
        {
          id: 'doi-judenken',
          x: 0.46,
          y: 0.42,
          title: '国が選んだ武家町の町並み',
          body: '文化庁の重要伝統的建造物群保存地区一覧によれば、安芸市土居廓中は武家町として、平成24年7月9日に重要伝統的建造物群保存地区に選定されています。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '土居廓中 高知県安芸市土居',
        },
        {
          id: 'doi-ikegaki',
          x: 0.74,
          y: 0.34,
          title: '土用竹の生垣は弓矢にも',
          body: '安芸市の公式サイトによれば、特徴的なのはウバメガシや土用竹の生垣、瓦と玉石を使った塀です。土用竹の生垣は暑さや寒さ、強風を防ぐ役割がありますが、ここでは、いざという時、弓矢として使うために植えられたといわれています。',
          source: 'https://www.city.aki.kochi.jp/life/dtl.php?hdnKey=43',
          mapQuery: '土居廓中 武家屋敷',
        },
        {
          id: 'doi-neribei',
          x: 0.22,
          y: 0.5,
          title: '割れ瓦を積み上げた塀',
          body: '安芸市の公式サイトによれば、割れ瓦を積み重ねた練り塀は瓦の産地ならではのもので、安芸地域ではよく見られます。多くは幕末から近代中期にかけて造られました。町割りはおよそ330年前に整えられたものです。',
          source: 'https://www.city.aki.kochi.jp/life/dtl.php?hdnKey=43',
          mapQuery: '土居廓中 練り塀',
        },
      ],
    },
    {
      id: 'ashizuri-misaki',
      name: '足摺岬',
      area: '土佐清水市・冬の午後',
      image: '/assets/scenery/kochi/scenes/09_ashizuri_misaki.webp',
      hotspots: [
        {
          id: 'ashizuri-sainantan',
          x: 0.46,
          y: 0.42,
          title: '四国のいちばん南のはし',
          body: '土佐清水市の公式サイトによれば、足摺岬は四国最南端の岬で、岬の西の臼碆は、日本列島で黒潮が最初に接岸する場所です。展望台からの視界は270度で、東は足摺岬から西は叶崎までが一望できます。',
          source: 'https://www.city.tosashimizu.kochi.jp/kanko/g01_ashizurimisaki.html',
          mapQuery: '足摺岬 高知県土佐清水市足摺岬',
        },
        {
          id: 'ashizuri-arch',
          x: 0.74,
          y: 0.34,
          title: '水平線がアーチに見える',
          body: '土佐清水市の公式サイトによれば、足摺岬の展望台からは、彼方にのぞむ水平線がアーチ状に見え、地球が丸いことが実感できるとされています。',
          source: 'https://www.city.tosashimizu.kochi.jp/kanko/g01_ashizurimisaki.html',
          mapQuery: '足摺岬展望台',
        },
        {
          id: 'ashizuri-tsubaki',
          x: 0.22,
          y: 0.5,
          title: '亜熱帯の緑と、赤い椿',
          body: '環境省の足摺宇和海国立公園の公式ページによれば、足摺岬周辺ではビロウ、クワズイモ、リュウビンタイ、アコウなどの亜熱帯植物が生育し、足摺岬のシンボルであるヤブツバキも多く、赤い花を咲かせています。',
          source: 'https://www.env.go.jp/park/ashizuri/point/index.html',
          mapQuery: '足摺岬 椿のトンネル',
        },
      ],
    },
    {
      id: 'katsurahama',
      name: '桂浜',
      area: '高知市・夜',
      image: '/assets/scenery/kochi/scenes/10_katsurahama.webp',
      hotspots: [
        {
          id: 'katsurahama-yumi',
          x: 0.46,
          y: 0.46,
          title: '弓なりに広がる浜',
          body: '高知市の公式サイトは、桂浜を、浦戸湾口にある弓状に広がる海岸と説明しています。その南端にあたるのが龍王岬で、岬の上には展望台と方位盤が置かれています。',
          source: 'https://www.city.kochi.kochi.jp/site/kanko/ryuomisaki.html',
          mapQuery: '桂浜 高知県高知市浦戸',
        },
        {
          id: 'katsurahama-tsuki',
          x: 0.74,
          y: 0.3,
          title: '月の名所として名高い',
          body: '高知市の公式サイトによれば、雄大な太平洋に面している桂浜は、月の名所としても名高く、よさこい節にも唄われている景勝地です。高知市の都市公園でもあり、遊歩道が整えられています。',
          source: 'https://www.city.kochi.kochi.jp/site/kanko/katsurahamakouen.html',
          mapQuery: '桂浜公園',
        },
        {
          id: 'katsurahama-namiuchigiwa',
          x: 0.22,
          y: 0.5,
          title: '波打ちぎわには近づけない',
          body: '高知市の公式サイトは、桂浜公園内の海岸付近は波の変化が激しく、急激に水深が深くなっていることから、波打ち際には絶対に近づかないようお願いします、と注意をうながしています。',
          source: 'https://www.city.kochi.kochi.jp/site/kanko/katsurahamakouen.html',
          mapQuery: '桂浜公園 駐車場',
        },
      ],
    },
  ],
};
